import React, {FormEvent, Fragment, FunctionComponent, useEffect, useState} from 'react'
import {Input} from "@App/Components/common/Input";
import {NumberPlateInput} from "@App/Components/NumberPlateInput";
import {Checkbox} from "@App/Components/common/Checkbox";
import {useCreateInquiry} from "@App/hooks/api/mutations/useCreateInquiry";
import {Loader} from "@App/Components/Loader";
import {useTranslation} from "react-i18next";
import {PhoneInput} from "@App/Components/common/PhoneInput";
import {useDispatch, useSelector} from "react-redux";
import {actionCreators, State} from "@App/state";
import {bindActionCreators} from "@reduxjs/toolkit";
import {MileageInput} from "@App/Components/common/MileageInput";
import {useNumberPlate} from "@App/hooks/useNumberPlate";
import {Modal} from "@App/Components/Modal";
import {useDates} from "@App/hooks/useDates";
import {
    PremiumServiceIsCollectAndReturn,
    PremiumServiceIsRentalCar,
    ServiceIsCertificate,
    ServiceIsVehicleReturn,
    ServiceIsWheelChange,
    ServiceRequiresAdditionalInformation
} from "@App/utils/conditionals/services";
import {
    OrganizationIsArval,
    OrganizationIsAyvens,
    OrganizationIsMeinAuto,
    OrganizationIsMobilityConcept,
    OrganizationIsRepareo
} from "@App/utils/conditionals/organization";
import {CaretDownIcon} from "@App/Components/common/Icons";
import {
    ActiveInquiryExistsForNumberplate,
    ActiveInquiryExistsForNumberplateAndService,
    CustomerAddressNotGiven,
    EarliestReturnDateNotReached,
    getEarliestReturnDate,
    MeinAutoCustomerContractNotFound,
    MeinAutoNoTyreProductInCustomerContract,
    NoDriverFoundForNumberPlate,
    NumberPlateNotFound,
    SlotAlreadyBlocked,
    SlotAlreadyBooked,
    VehicleNotFoundInExternalFleet
} from "@App/utils/conditionals/errors";
import {GarageHasNationwideService} from "@App/utils/conditionals/garages";
import {RentalCarSelect} from "@App/Components/common/RentalCarSelect";

type ContactFormProps = {
    onSuccessful: () => void
}

export const ContactForm: FunctionComponent<ContactFormProps> = ({onSuccessful}: ContactFormProps) => {

    const {t} = useTranslation()
    const organization = useSelector((state: State) => state.orders.organization)
    const {formatDate} = useDates()

    const firstName = useSelector((state: State) => state.orders.firstName)
    const lastName = useSelector((state: State) => state.orders.lastName)
    const email = useSelector((state: State) => state.orders.email)
    const phone = useSelector((state: State) => state.orders.phone)

    const dispatch = useDispatch();
    const {setFirstName, setLastName, setEmail, setPhone, setMileage, setSalutation, setNumberPlate} = bindActionCreators(actionCreators, dispatch)

    const [street, setStreet] = useState('')
    const [city, setCity] = useState('')
    const [postcode, setPostcode] = useState('')
    const [addition, setAddition] = useState('')

    const [numberPlateChecking, setNumberPlateChecking] = useState(false)

    const [tooltipShowing, setTooltipShowing] = useState(false)

    const [tos, setTos] = useState(false)
    const [marketing, setMarketing] = useState(false)
    const [adacMember, setAdacMember] = useState(false)
    const [message, setMessage] = useState('')
    const numberPlate = useSelector((state: State) => state.orders.numberPlate)

    const {formatNumberPlate} = useNumberPlate()
    const formattedNumberPlate = formatNumberPlate(numberPlate)

    const mileage = useSelector((state: State) => state.orders.mileage)
    const premiumServices = useSelector((state: State) => state.orders.premiumServices)

    const salutation = useSelector((state: State) => state.orders.salutation)

    const [showNumberPlateDialog, setShowNumberPlateDialog] = useState(false)

    const {
        service,
        garage,
        error,
        loading,
        inquiry,
        validationErrors,
        checkNumberPlate,
        submit
    } = useCreateInquiry({
        salutation,
        firstName,
        lastName,
        email,
        phone,
        tos,
        marketing,
        adacMember,
        street,
        city,
        postcode,
        addition,
        message,
        numberPlate,
        mileage
    })

    useEffect(() => {
        if (inquiry && inquiry.id) {
            onSuccessful && onSuccessful()
        }
    }, [inquiry])


    const getPlaceholder = () => {
        let placeholderMessage = ''
        if (ServiceRequiresAdditionalInformation(service)) {
            placeholderMessage = t('contactForm.placeholders.message-required')
        } else if (ServiceIsWheelChange(service)) {
            placeholderMessage = t('contactFormMessages.wheeSizeDisclaimer')
        } else if (OrganizationIsRepareo(organization)) {
            placeholderMessage = t('contactFormMessages.registrationDocumentDisclaimer')
        } else {
            placeholderMessage = t('contactForm.placeholders.message')
        }
        return placeholderMessage
    }

    useEffect(() => {

    }, [])

    if (loading || !service || !garage) {
        return <div className="grid place-items-center">
            <Loader/>
        </div>
    }

    const submitForm = (e: FormEvent) => {
        e.preventDefault()
        if (numberPlate.firstID.length > 0 && numberPlate.secondID.length > 0 && numberPlate.region.length > 0) {
            setNumberPlateChecking(true)
            checkNumberPlate()
                .then((res) => {
                    setNumberPlateChecking(false)
                    res.data.numberPlateCheck.status ? submit(e) : setShowNumberPlateDialog(true)
                })
        } else {
            submit(e)
        }
    }

    const submitWithMissingNumberPlate = (e: FormEvent) => {
        setShowNumberPlateDialog(false)
        submit(e)
    }

    return (
        <Fragment>
            <form onSubmit={(e) => submitForm(e)}>
                {showNumberPlateDialog && (
                    <Modal open={showNumberPlateDialog} onClose={() => setShowNumberPlateDialog(false)}
                           title={t('contactForm.numberCheck.title')} width="3xl" showButton={false}>
                        <div>{t('contactForm.numberCheck.text', {numberplate: formattedNumberPlate})}</div>
                        <div className="flex flex-col md:flex-row justify-between mt-10">
                            <button
                                type="button"
                                onClick={() => setShowNumberPlateDialog(false)}
                                className={`bg-warning w-full mb-6 md:mb-0 md:w-80 px-8 py-2 mt-4 text-white rounded text-center font-serif font-bold cursor-pointer no-underline focus:outline-none`}
                            >{t('contactFormMessages.numberplateChange')}
                            </button>
                            <button
                                type="button"
                                onClick={(e) => submitWithMissingNumberPlate(e)}
                                className={`bg-success w-full md:w-80 px-8 py-2 mt-4 text-white rounded text-center font-serif font-bold cursor-pointer no-underline focus:outline-none`}
                            >{t('contactFormMessages.numberplateCorrect')}
                            </button>
                        </div>
                    </Modal>
                )}
                {error && (SlotAlreadyBooked(error) || SlotAlreadyBlocked(error)) && (
                    <Fragment>
                        <Modal open={true}
                               title={t('contactFormMessages.slotAlreadyTakenTitle')} width="3xl" showButton={false}>
                            <div>{t('contactForm.slot-out-of-stock')}</div>
                        </Modal>
                        <div className="errorWhenBlockingSlot rounded-md bg-yellow-50 p-4 mb-6">
                            <div className="flex">
                                <div>
                                    <div className="text-yellow-700">
                                        <p>
                                            {t('contactForm.slot-out-of-stock')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Fragment>
                )}
                {error && ActiveInquiryExistsForNumberplate(error) && (
                    <Fragment>
                        <Modal open={true}
                               title={t('contactFormMessages.activeInquiryExistsForNumberplateErrorMessageTitle')}
                               width="3xl" showButton={false}>
                            <div>{t('contactForm.activeInquiryExistsForNumberplateErrorMessage')}</div>
                        </Modal>
                        <div className="rounded-md bg-yellow-50 p-4 mb-6">
                            <div className="flex">
                                <div>
                                    <div className="text-yellow-700">
                                        <p>
                                            {t('contactForm.activeInquiryExistsForNumberplateErrorMessage')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Fragment>
                )}
                {error && ActiveInquiryExistsForNumberplateAndService(error) && (
                    <Fragment>
                        <Modal open={true}
                               title={t('contactFormMessages.activeInquiryExistsForNumberplateAndServiceErrorMessageTitle')}
                               width="3xl" showButton={false}>
                            <div>{t('contactForm.activeInquiryExistsForNumberplateAndServiceErrorMessage')}</div>
                        </Modal>
                        <div className="rounded-md bg-yellow-50 p-4 mb-6">
                            <div className="flex">
                                <div>
                                    <div className="text-yellow-700">
                                        <p>
                                            {t('contactForm.activeInquiryExistsForNumberplateAndServiceErrorMessage')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Fragment>
                )}
                {error && EarliestReturnDateNotReached(error) && (
                    <Fragment>
                        <Modal open={true}
                               title={t('contactFormMessages.returnDateNotReachedTitle')} width="3xl"
                               showButton={false}>
                            <div
                                dangerouslySetInnerHTML={{__html: t('contactFormMessages.returnDateNotReached', {returnDate: formatDate(getEarliestReturnDate(error), 'dd.MM.yyyy')})}}/>
                        </Modal>
                        <div id={'returnDateNotReachedError'} className="rounded-md bg-yellow-50 p-4 mb-6">
                            <div className="flex">
                                <div>
                                    <div className="text-yellow-700">
                                        <p dangerouslySetInnerHTML={{__html: t('contactFormMessages.returnDateNotReached', {returnDate: formatDate(getEarliestReturnDate(error), 'dd.MM.yyyy')})}}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Fragment>
                )}
                {error && (NumberPlateNotFound(error) || VehicleNotFoundInExternalFleet(error) || NoDriverFoundForNumberPlate(error)) && (
                    <Fragment>
                        <Modal open={true}
                               title={t('contactFormMessages.numberplateNotFoundTitle')} width="3xl" showButton={false}>
                            <div
                                dangerouslySetInnerHTML={{__html: t('contactFormMessages.numberplateNotFoundMessage')}}/>
                        </Modal>
                        <div className="rounded-md bg-yellow-50 p-4 mb-6">
                            <div className="flex">
                                <div>
                                    <div className="text-yellow-700">
                                        <p dangerouslySetInnerHTML={{__html: t('contactFormMessages.numberplateNotFoundMessage')}}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Fragment>
                )}
                {error && (MeinAutoCustomerContractNotFound(error) || MeinAutoNoTyreProductInCustomerContract(error)) && (
                    <Fragment>
                        <Modal open={true}
                               title={t('contactFormMessages.meinautoContractDoesNotProvideServiceErrorMessageTitle')} width="3xl" showButton={false}>
                            <div
                                dangerouslySetInnerHTML={{__html: t('contactFormMessages.meinautoContractDoesNotProvideServiceErrorMessage')}}/>
                        </Modal>
                        <div className="rounded-md bg-yellow-50 p-4 mb-6">
                            <div className="flex">
                                <div>
                                    <div className="text-yellow-700">
                                        <p dangerouslySetInnerHTML={{__html: t('contactFormMessages.meinautoContractDoesNotProvideServiceErrorMessage')}}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Fragment>
                )}
                {error && CustomerAddressNotGiven(error) && (
                    <Fragment>
                        <Modal open={true}
                               title={t('contactFormMessages.customerAddressNotGivenTitle')} width="3xl"
                               showButton={false}>
                        </Modal>
                        <div id={'CustomerAddressNotGivenError'} className="rounded-md bg-yellow-50 p-4 mb-6">
                            <div className="flex">
                                <div>
                                    <div className="text-yellow-700">
                                        <p>{t('contactFormMessages.customerAddressNotGiven')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Fragment>
                )}
                <div className={'relative flex items-center justify-between w-1/4 md:w-1/6 my-2'}>
                    <select
                        id="salutation"
                        name={'salutation'}
                        className={'w-full appearance-none cursor-pointer border border-neutral-500 bg-white rounded shadow py-2 px-3'}
                        value={salutation}
                        onChange={(e) => setSalutation(parseInt(e.target.value))}>
                        <option value="0">Herr</option>
                        <option value="1">Frau</option>
                    </select><CaretDownIcon className={'absolute w-3 h-w right-3'} color={'neutral-500'}/>
                </div>
                <Input id={"first-name"} name="first-name" className={"w-full"}
                       placeholder={t('contactForm.placeholders.first-name')}
                       value={firstName}
                       onChange={setFirstName}
                       validationMessage={validationErrors.firstName ? t('contactForm.validationErrors.first-name') : null}
                       required={true}
                />
                <Input id={"last-name"} name="last-name" className={"w-full"}
                       placeholder={t('contactForm.placeholders.last-name')} value={lastName}
                       onChange={setLastName}
                       validationMessage={validationErrors.lastName ? t('contactForm.validationErrors.last-name') : null}
                       required={true}
                />
                <Input id={"email"}
                       type={"email"}
                       name="email"
                       className={"w-full"}
                       placeholder={t('contactForm.placeholders.email')}
                       value={email}
                       onChange={setEmail}
                       validationMessage={validationErrors.email ? t('contactForm.validationErrors.email') : null}
                       required={true}
                />
                <PhoneInput id={"phone"}
                            type={"tel"}
                            name="phone"
                            className={"w-full"}
                            placeholder={t('contactForm.placeholders.phone')}
                            value={phone}
                            onChange={setPhone}
                            validationMessage={validationErrors.phone ? t('contactForm.validationErrors.phone') : null}
                />
                {(premiumServices.some((premiumService) => PremiumServiceIsCollectAndReturn(premiumService)) || GarageHasNationwideService(garage)) && (
                    <Fragment>
                        <div
                            className="font-bold mt-5">{GarageHasNationwideService(garage) ? t('contactFormMessages.nationwideServiceAddressHeadline') : t('contactForm.address-label')}</div>
                        <Input id={"street"}
                               name="street"
                               className={"w-full"}
                               placeholder={t('contactForm.placeholders.street')}
                               value={street}
                               onChange={setStreet}
                               required={(premiumServices.some((premiumService) => PremiumServiceIsCollectAndReturn(premiumService)))}
                        />
                        <Input id={"additional"}
                               name="additional"
                               className={"w-full"}
                               placeholder={t('contactForm.placeholders.addition')}
                               value={addition}
                               onChange={setAddition}
                               required={(premiumServices.some((premiumService) => PremiumServiceIsCollectAndReturn(premiumService)))}
                        />
                        <Input id={"postcode"}
                               name="postcode"
                               className={"w-full"}
                               placeholder={t('contactForm.placeholders.postcode')}
                               value={postcode}
                               onChange={setPostcode}
                               required={(premiumServices.some((premiumService) => PremiumServiceIsCollectAndReturn(premiumService)))}
                        />
                        <Input id={"city"}
                               name="city"
                               className={"w-full"}
                               placeholder={t('contactForm.placeholders.city')}
                               value={city}
                               onChange={setCity}
                               required={(premiumServices.some((premiumService) => PremiumServiceIsCollectAndReturn(premiumService)))}
                        />
                    </Fragment>
                )}
                <div className="font-bold mt-5">{t('contactForm.number-plate-label')}</div>
                <NumberPlateInput
                    value={numberPlate}
                    onChange={setNumberPlate}
                    validationMessage={validationErrors.numberPlate ? t('contactForm.validationErrors.number-plate') : null}
                    showNumberPlateSyncMessage={OrganizationIsArval(organization)}
                />

                <div className="font-bold mt-5">{t('contactForm.mileage-label')}</div>
                <MileageInput
                    value={mileage}
                    onChange={setMileage}
                    placeholder="30.000"
                    className="w-full"
                    validationMessage={validationErrors.mileage ? t('contactForm.validationErrors.mileage') : null}
                />

                {(OrganizationIsAyvens(organization) && premiumServices.some((premiumService) => PremiumServiceIsRentalCar(premiumService))) && (
                    <Fragment>
                        <div className="font-bold mt-5">{t('contactForm.rentalCar-label')}</div>
                        <RentalCarSelect/>
                    </Fragment>
                )}

                <div
                    className="font-bold mt-5">{service.requires_additional_information ? t('contactForm.message-label-required', {garageType: garage.garageType.display_name_singular}) : t('contactForm.message-label', {garageType: garage.garageType.display_name_singular})}</div>
                <textarea name="customerMessage"
                          id="customerMessage"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          className={"border border-neutral-500 rounded shadow py-2 px-3 my-2 w-full"}
                          cols={30}
                          rows={3}
                          maxLength={1000}
                          placeholder={getPlaceholder()}
                />
                {validationErrors.message && (
                    <p className="mt-1 mb-4 text-sm text-error-600">
                        {t('contactForm.validationErrors.message')}
                    </p>
                )}

                <div className="flex mt-3">
                    <Checkbox id={"legal"} status={tos} onChange={setTos}/>
                    <label htmlFor={"legal"}>
                        Ich stimme den <a
                        href="https://www.repareo.de/allgemeine-nutzungsbedingungen-fuer-repareo/"
                        target="_blank">Nutzungsbedingungen</a> und
                        der <a
                        href="https://www.repareo.de/datenschutzerklaerung/"
                        target="_blank">Datenschutzerklärung</a> zu.
                        Meine Kontaktdaten dürfen für Rückfragen zu den Terminen genutzt werden.* Widerruf
                        jederzeit möglich.
                    </label>
                </div>
                {validationErrors.tos && (
                    <div className={'mb-3 text-sm text-error'}>
                        {t('contactForm.validationErrors.tos')}
                    </div>
                )}

                {OrganizationIsRepareo(organization) && (
                    <Fragment>
                        <div className="flex mt-3">
                            <Checkbox id={"marketing"} status={marketing} onChange={setMarketing}/>
                            <label htmlFor={"marketing"}>
                                {t('contactForm.marketing')}
                            </label>
                        </div>
                        {!ServiceIsCertificate(service) && (
                            <div className="flex mt-3 items-center relative">
                                <Checkbox id={"adacMember"} status={adacMember} onChange={setAdacMember}/>
                                <label htmlFor={"adacMember"} className={'mb-2'}>Ich bin ADAC Mitglied und möchte an
                                    der <span className={'border-b border-dashed cursor-pointer'}
                                              onMouseOver={() => setTooltipShowing(true)}
                                              onMouseLeave={() => setTooltipShowing(false)}>ADAC 3 % Cashback-Action</span> teilnehmen.</label>
                                {tooltipShowing && (
                                    <div>
                                        <p className={'absolute text-sm rounded-md w-[300px] right-0 md:left-80 top-10 p-2 bg-white shadow z-20'}>
                                            {t('contactForm.adac-tip')}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </Fragment>
                )}

                <button type="submit"
                        disabled={numberPlateChecking}
                        className={`w-full bg-primary px-8 py-2 mt-5 sm:w-72 text-white rounded text-center font-bold font-serif cursor-pointer ${numberPlateChecking ? 'bg-primary-200' : ''}`}>
                    {numberPlateChecking
                        ? t('contactForm.submitting')
                        : (service && ServiceIsVehicleReturn(service)
                            ? t('contactForm.vehicleReturn.submit')
                            : t('contactForm.submit'))}
                </button>

                {(OrganizationIsMobilityConcept(organization) || OrganizationIsMeinAuto(organization)) && (
                    <div className={"text-sm italic font-bold mt-3 text-primary"} dangerouslySetInnerHTML={{__html: (t('generalWords.hint') + '' + t('booking.hint'))}}></div>
                )}
                <div className={"text-sm italic mt-3"}>{t('contactForm.mandatory-field-notice')}</div>
            </form>
        </Fragment>
    )
}
